import { looksLikeAddress, personName } from '@akpl/shared';

/**
 * The name the Home greeting is allowed to say (QA 2026-09-15 UX-125, 2026-09-16 UX-400).
 *
 * The phone used to greet "Hola, carlos+dev": nothing on the client invented that string — the
 * API derived a member's name from the address when the account had no `display_name`, so the
 * raw e-mail local part — plus-tag and all — travelled into `members/{uid}.name` and straight
 * onto the greeting. This module guarded against it; the API's own helper did not, which is why
 * iteration 3 found the greeting right ("Hola, Carlos") and every other surface wrong ("Pidió
 * carlos+dev").
 *
 * The rule now lives once, in `@akpl/shared`'s {@link personName}, and both sides call it. What
 * is left here is the greeting's own contract: it answers `null` rather than `''`, because the
 * caller greets with a bare "Hola", never "Hola, Invitado".
 */
export interface GreetingSource {
  /** `GET /me` → `user.displayName`. */
  displayName?: string | null;
  /** The live `members/{uid}.name` node, which is what a guest typed when they joined. */
  memberName?: string | null;
  /** `GET /me` → `user.email`. */
  email?: string | null;
}

export function greetingName(source: GreetingSource | null | undefined): string | null {
  const email = source?.email?.trim() || null;
  const candidates = [source?.displayName, source?.memberName]
    .map((value) => value?.trim() ?? '')
    .filter(Boolean);
  // A name somebody chose wins, in order; one that is the address (or a fragment) is not one.
  for (const candidate of candidates) {
    if (!looksLikeAddress(candidate, email)) return candidate;
  }
  return personName(null, email ?? candidates[0] ?? null) || null;
}
